
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProfileFormValues } from "./types";
import { formatUrl } from "./useProfileFormUtils";
import { UseFormReturn } from "react-hook-form";
import { Profile } from "@/services/profiles";

export const useProfileFormSubmit = (
  form: UseFormReturn<ProfileFormValues>,
  updateUserProfile: (updates: Partial<ProfileFormValues>) => Promise<Profile | null>,
  refreshProfile: () => Promise<void>,
  user: any
) => {
  const handleSubmit = async (data: ProfileFormValues) => {
    if (!user) {
      toast.error("You must be logged in to update your profile");
      return;
    }
    
    try {
      // Format URLs properly before saving
      const formattedLinkedInUrl = formatUrl(data.linkedin_url);
      const formattedWebsiteUrl = formatUrl(data.website_url);
      
      // Update profile details
      const profileUpdates = {
        display_username: data.display_username,
        university_id: data.university_id,
        university: data.university,
        lab_id: data.lab_id,
        lab: data.lab,
        city: data.city,
        country: data.country,
        research_interests: data.research_interests,
        expertise: data.expertise,
        linkedin_url: formattedLinkedInUrl,
        website_url: formattedWebsiteUrl,
        email_notifications: data.email_notifications,
        notify_swap_offers: data.notify_swap_offers,
        notify_swap_responses: data.notify_swap_responses,
        daily_digest_email: data.daily_digest_email,
        notify_messages: data.notify_messages,
        weekly_digest_email: data.weekly_digest_email,
      };
      
      await updateUserProfile(profileUpdates);
      
      // Update email if changed
      if (data.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: data.email
        });
        
        if (emailError) {
          toast.error("Failed to update email: " + emailError.message);
        } else {
          toast.success("Email update initiated. Please check your inbox for confirmation.");
        }
      }
      
      // Update password only if a new password is provided
      if (data.newPassword && data.newPassword.trim() !== '') {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: data.newPassword
        });
        
        if (passwordError) {
          toast.error("Failed to update password: " + passwordError.message);
        } else {
          toast.success("Password updated successfully");
          // Clear password fields after successful update
          form.setValue("newPassword", "");
          form.setValue("confirmPassword", "");
        }
      }
      
      toast.success("Profile updated successfully");
      refreshProfile();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  return handleSubmit;
};
